/**
 * Incarichi guidati all'Institore e risultati dei messaggi (ADR 0035).
 *
 * Un incarico è una richiesta strutturata: il backend la valida e la trasforma in un
 * messaggio normale al relay, con il testo composto da lui. La risposta è il messaggio in
 * coda, come per `sendMessage`.
 *
 * Le operazioni stanno in una classe base di `AgentsApi`, così le pagine le trovano sul
 * client degli agenti senza un client nuovo nel contesto.
 */

import type { ApiClient } from './client';
import type { SetMessage } from './agentTypes';

/** Lingue dei video e dei report. */
export type GuidedLanguage = 'it' | 'en' | 'es' | 'fr' | 'de';

/** Lingue dei rapporti degli specialisti (dal 2026-10-31 anche quelle nuove dell'interfaccia). */
export type ReportLanguage = 'it' | 'en' | 'de' | 'es' | 'fr' | 'ru' | 'ko' | 'th';

/** Lingue dei rapporti, nell'ordine del selettore. */
export const REPORT_LANGUAGES: readonly ReportLanguage[] = ['it', 'en', 'de', 'es', 'fr', 'ru', 'ko', 'th'];

/**
 * Lingua del rapporto proposta all'apertura: quella dell'interfaccia.
 *
 * @param language Lingua dell'interfaccia.
 * @returns La lingua del rapporto; l'italiano per una lingua sconosciuta.
 */
export function initialReportLanguage(language: string): ReportLanguage {
  return (REPORT_LANGUAGES as readonly string[]).includes(language) ? (language as ReportLanguage) : 'it';
}

/** Voci del catalogo della voce fuori campo; `none` per i soli sottotitoli. */
export type GuidedVoice = 'giusy' | 'alice' | 'george' | 'none';

/** Profondità della ricerca. */
export type ResearchEffort = 'low' | 'medium' | 'high';

/** Risposta a una domanda sì, no, non so. */
export type GuidedAnswer = 'yes' | 'no' | 'unknown';

/** Destinazione dell'applicazione. */
export type ReleaseTarget = 'institor' | 'cloud' | 'mobile_store' | 'unknown';

/** Utenti attesi al lancio. */
export type ExpectedUsers = 'under_100' | 'under_10k' | 'over_10k' | 'unknown';

/** Un'azione della sequenza del video. */
export interface VideoStep {
  /** Che cosa succede sullo schermo, al più 300 caratteri. */
  action: string;
  /** Che cosa dice la voce, al più 400 caratteri; vuota per un'azione muta. */
  narration: string;
}

/** Video demo, fase 1: brief per la proposta. */
export interface VideoPlanBody {
  /** Tipo di incarico. */
  kind: 'video_plan';
  /** Progetto del set da mostrare. */
  project_id: string;
  /** Che cosa deve mostrare il video. */
  goal: string;
  /** Per chi è. */
  audience?: string;
  /** Da dove partire. */
  start_url?: string;
  /** Note. */
  notes?: string;
}

/** Video demo, fase 2: sequenza rivista, lingue, durata e voce. */
export interface VideoProductionBody {
  /** Tipo di incarico. */
  kind: 'video_production';
  /** Progetto del set. */
  project_id: string;
  /** Azioni, da 1 a 30. */
  steps: VideoStep[];
  /** Lingue, da 1 a 3. */
  languages: GuidedLanguage[];
  /** Durata di ogni video, da 20 a 300 secondi. */
  duration_seconds: number;
  /** Voce. */
  voice: GuidedVoice;
  /** Note. */
  notes?: string;
}

/** Ricerca su un argomento. */
export interface ResearchBody {
  /** Tipo di incarico. */
  kind: 'research';
  /** Argomento o domanda. */
  topic: string;
  /** Profondità. */
  effort: ResearchEffort;
  /** Lingua del report: italiano o inglese. */
  report_language: ReportLanguage;
  /** Decisione da sostenere. */
  decision?: string;
  /** Vincoli e fonti. */
  constraints?: string;
  /** Progetto, facoltativo. */
  project_id?: string;
}

/** Controllo prima della produzione. */
export interface ReleaseCheckBody {
  /** Tipo di incarico. */
  kind: 'release_check';
  /** Progetto da controllare. */
  project_id: string;
  /** Destinazione. */
  target: ReleaseTarget;
  /** Utenti attesi. */
  expected_users: ExpectedUsers;
  /** Dati personali. */
  personal_data: GuidedAnswer;
  /** Pagamenti. */
  payments: GuidedAnswer;
  /** Data di lancio prevista, testo libero. */
  launch_date?: string;
  /** Note. */
  notes?: string;
}

/** Piattaforma da confrontare con i competitor. */
export type CompetitorPlatform = 'web' | 'android' | 'ios';

/**
 * Confronto con i competitor (ADR 0060): l'Institore lo passa allo specialista della
 * piattaforma, e il risultato arriva in chat con il rapporto in allegato.
 */
export interface CompetitorsBody {
  /** Tipo di incarico. */
  kind: 'competitors';
  /** Progetto da confrontare. */
  project_id: string;
  /** Settore e prodotto in una frase, al più 300 caratteri. */
  sector: string;
  /** Paese o mercato, al più 100 caratteri. */
  market: string;
  /** Profondità. */
  effort: ResearchEffort;
  /** Lingua del rapporto. */
  report_language: ReportLanguage;
  /** Competitor indicati dall'utente, nomi o indirizzi, al più 5. */
  competitors?: string[];
  /** Piattaforme da confrontare. */
  platforms?: CompetitorPlatform[];
  /** Domanda o obiettivo. */
  question?: string;
}

/** Corpo di `POST /api/v1/agent-sets/{set_id}/guided-requests`. */
export type GuidedRequestBody = VideoPlanBody | VideoProductionBody | ResearchBody | ReleaseCheckBody | CompetitorsBody;

/** Un file consegnato da un agente sotto la risposta (ADR 0035 §6). */
export interface MessageOutput {
  /** Identificativo. */
  output_id: string;
  /** Nome del file. */
  name: string;
  /** Tipo. */
  media_type: string;
  /** Dimensione in byte. */
  bytes: number;
}

/**
 * Percorso di scaricamento di un risultato. Si apre come link: la sessione viaggia nel
 * cookie, come per l'esportazione dei dati, e nessun token finisce nell'indirizzo.
 *
 * @param setId Set.
 * @param messageId Messaggio.
 * @param outputId Risultato.
 * @returns Il percorso relativo al gateway.
 */
export function outputDownloadPath(setId: string, messageId: string, outputId: string): string {
  return (
    `/api/v1/agent-sets/${encodeURIComponent(setId)}/messages/${encodeURIComponent(messageId)}` +
    `/outputs/${encodeURIComponent(outputId)}`
  );
}

/** Operazioni degli incarichi, base di `AgentsApi`. */
export class GuidedRequestsApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(protected readonly client: ApiClient) {}

  /**
   * Affida un incarico all'Institore.
   *
   * @param setId Set.
   * @param body Incarico.
   * @returns Il messaggio in coda, con il testo composto dal backend.
   * @throws ApiError 422 `project_not_in_set`, 409 `queue_full` o `token_limit_reached`.
   */
  sendGuidedRequest(setId: string, body: GuidedRequestBody): Promise<SetMessage> {
    return this.client.request<SetMessage>(
      'POST',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/guided-requests`,
      body,
    );
  }
}
